import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { dispatchWithToast, HtmlTextFormatter, dateToString } from "../../Utils";
import { updateWorkOrderAsync } from "../../../slices/workOrder";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { DocumentViewer } from "../Media/documentViewer";
import { WorkOrderOperationStatus } from "./utils";
import { PMDialog } from "../../utils/PMDialog";
import { PMNotice } from "../../utils/notifications";
import { PMButton } from "../../utils/PMButton";

function SectionCard({ title, description, children, className = "" }) {
  return (
    <div
      className={`shadow sm:overflow-hidden sm:rounded-md border border-gray-200 ${className}`}
    >
      <div className="space-y-4 bg-white py-5 px-4 sm:p-6">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {title}
          </h3>
          {description ? (
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function WorkOrderFinishDialog(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { open, onClose, selected, fields, isLoading } = props;

  const workOrder = selected;

  /** True when API reports a blocking `finish_work_order` operation (`can_be_completed: false`). */
  const hasFinishBlockers = () => {
    const ops = workOrder?.operations;
    if (!Array.isArray(ops)) return false;
    return ops.some(
      (operation) =>
        operation.name === "finish_work_order" &&
        operation.can_be_completed === false
    );
  };

  const isDone = Boolean(workOrder?.is_completed);
  const blocked = !isDone && hasFinishBlockers();
  /** Klart til bekreftelse: ikke ferdigstilt og ingen blokkerende krav. */
  const canConfirmFinish = !isDone && !blocked;
  /** Vis detaljkort (som på lesesiden): fullført AO, eller klar til ferdigstilling. */
  const showSummarySections = isDone || canConfirmFinish;

  function completionChecklist() {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <WorkOrderOperationStatus
          operations={workOrder?.operations}
          operationName="missing_participants"
          title="Deltakere"
        />
        <WorkOrderOperationStatus
          operations={workOrder?.operations}
          operationName="missing_description"
          title="Beskrivelse"
        />
        <WorkOrderOperationStatus
          operations={workOrder?.operations}
          operationName="missing_participant_notes"
          title={t("work_order.completion.check_participant_notes", {
            defaultValue: "Oppdatering / notater fra deltakere",
          })}
        />
        <WorkOrderOperationStatus
          operations={workOrder?.operations}
          operationName="missing_items"
          title="Vareforbruk"
        />
        <WorkOrderOperationStatus
          operations={workOrder?.operations}
          operationName="missing_hours"
          title="Timeregistrering"
        />
      </div>
    );
  }

  function ItemConsumptions() {
    if (!workOrder?.items?.length) {
      return (
        <p className="text-sm text-gray-500">Ingen varelinjer registrert.</p>
      );
    }
    return (
      <div className="overflow-x-auto -mx-4 sm:mx-0 border-t border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 sm:pl-6"
              >
                Artikkelnr.
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
              >
                Tittel
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 sm:pr-6"
              >
                Antall
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {workOrder.items.map((item) => (
              <tr key={item.id}>
                <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-600 sm:pl-6">
                  {item?.product?.item_number}
                </td>
                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                  {item?.product?.title
                    ? item?.product?.title
                    : item?.description}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-600 sm:pr-6">
                  {item?.quantity}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function Hours() {
    if (!workOrder?.hours?.length) {
      return <p className="text-sm text-gray-500">Ingen timer registrert.</p>;
    }
    return (
      <div className="overflow-x-auto -mx-4 sm:mx-0 border-t border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 sm:pl-6"
              >
                Dato
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600"
              >
                Ansatt
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600"
              >
                Timer
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 sm:pr-6"
              >
                Beskrivelse
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {workOrder.hours.map((hour) => (
              <tr key={hour.id}>
                <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-600 sm:pl-6">
                  {dateToString(hour.date)}
                </td>
                <td className="px-3 py-3 text-sm font-medium text-gray-900">
                  {hour?.user?.displayTitle}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-600">
                  {hour?.time}
                </td>
                <td className="px-3 py-3 text-sm text-gray-600 sm:pr-6">
                  {hour?.description}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function DetailsGrid() {
    const rows = [
      { label: "Kunde", value: workOrder?.customer?.displayTitle || "—" },
      { label: "Prosjekt", value: workOrder?.project?.displayTitle || "—" },
      { label: "Status", value: workOrder?.status || "—" },
    ];
    return (
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3 border-t border-gray-200 pt-4">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {row.label}
            </dt>
            <dd className="mt-1 text-sm font-medium text-gray-900">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  function Participants() {
    if (!workOrder?.participants?.length) {
      return (
        <p className="text-sm text-gray-500">Ingen deltakere registrert.</p>
      );
    }
    return (
      <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
        {workOrder.participants.map((participant) => (
          <li
            key={participant.id}
            className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
          >
            <span className="font-medium text-gray-900">
              {participant?.user?.displayTitle}
            </span>
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
              {participant?.role}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  function Notes() {
    if (!workOrder?.notes?.length) {
      return <p className="text-sm text-gray-500">Ingen notater ennå.</p>;
    }
    return (
      <div className="space-y-3">
        {workOrder.notes.map((note) => (
          <div
            key={note.id}
            className="rounded-lg border border-gray-100 bg-gray-50/80 p-4"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">
                {note?.created_by?.displayTitle}
              </span>
              <span className="text-xs text-gray-500">
                {dateToString(note?.created_at)}
              </span>
            </div>
            <div className="prose prose-sm mt-2 max-w-none text-gray-700">
              <HtmlTextFormatter string={note?.note || ""} />
            </div>
            {note?.documents && note?.documents.length > 0 && (
              <div className="mt-3">
                <DocumentViewer
                  pathType="quickview"
                  documents={note.documents}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={workOrder?.is_completed ? "Arbeidsordre" : "Ferdigstill arbeidsordre"}
      size="5xl"
    >
      <div className="w-full min-w-0">
        {isLoading ? (
          <ResourceLoading />
        ) : (
          <div className="space-y-6 pb-2">
            {!isDone && (
              <div>
                {blocked ? (
                  <PMNotice
                    severity="error"
                    title="Arbeidsordren er ikke klar for ferdigstilling"
                    description="Sjekk punktene under og oppdater arbeidsordren. Når alt er i orden, åpne dialogen på nytt."
                  />
                ) : (
                  <PMNotice
                    severity="success"
                    title="Arbeidsordren er klar for ferdigstilling"
                    description="Gå gjennom oppsummeringen under og bekreft når du vil ferdigstille."
                  />
                )}
              </div>
            )}

            {!isDone && (
              <SectionCard
                title="Krav før ferdigstilling"
                description="Status for obligatoriske og valgfrie opplysninger."
              >
                {completionChecklist()}
              </SectionCard>
            )}

            {showSummarySections && (
              <>
                <SectionCard title="Detaljer">
                  <DetailsGrid />
                </SectionCard>

                <SectionCard title="Beskrivelse">
                  {workOrder?.description ? (
                    <div className="prose prose-sm max-w-none border-t border-gray-200 pt-4 text-gray-700">
                      <HtmlTextFormatter string={workOrder.description} />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 border-t border-gray-200 pt-4">
                      Ingen beskrivelse.
                    </p>
                  )}
                </SectionCard>

                <SectionCard title="Deltagere">
                  <Participants />
                </SectionCard>

                <SectionCard title="Notater fra deltagere">
                  <Notes />
                </SectionCard>

                <SectionCard title="Vareforbruk">
                  <ItemConsumptions />
                </SectionCard>

                <SectionCard title="Timeregistrering">
                  <Hours />
                </SectionCard>
              </>
            )}

            {blocked && (
              <p className="text-center text-sm text-gray-500">
                Lukk dialogen, gjør endringene på arbeidsordren, og prøv igjen.
              </p>
            )}

            <Formik
              initialValues={{
                id: workOrder?.id
                  ? workOrder?.id
                  : fields?.id
                    ? fields?.id
                    : null,
                is_completed: true,
                submit: null,
              }}
              validationSchema={Yup.object({
                description: Yup.string().max(500),
              })}
              onSubmit={(values, helpers) => {
                try {
                  dispatchWithToast(
                    dispatch(updateWorkOrderAsync(values)),
                    props.onRefresh,
                    props.onClose
                  );

                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);
                } catch (err) {
                  toast.error("Something went wrong...");
                  console.error(err);
                  helpers.setStatus({ success: false });
                  helpers.setErrors({ submit: err.message });
                  helpers.setSubmitting(false);
                }
              }}
            >
              {(formik) => (
                <Form>
                  <div className="mt-6 border-t border-gray-200 bg-white pt-4">
                    {isDone ? (
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-gray-600">
                          Arbeidsordre ble ferdigstilt av{" "}
                          <span className="font-medium text-gray-900">
                            {workOrder?.updated_by?.displayTitle}
                          </span>
                          .
                        </p>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                          onClick={onClose}
                        >
                          {t("Close")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                          onClick={onClose}
                        >
                          {t("Close")}
                        </button>
                        {canConfirmFinish ? (
                          <PMButton
                            type="button"
                            styleType="primary"
                            className="!w-auto sm:!w-auto !bg-emerald-600 hover:!bg-emerald-700"
                            onClick={formik.submitForm}
                          >
                            {t("Finish")}
                          </PMButton>
                        ) : null}
                      </div>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
    </PMDialog>
  );
}
